import { createClient } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Outlet, redirect, useLocation } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { motion } from "motion/react";
import posthog from "posthog-js";
import { useEffect, useRef } from "react";
import { AuthForm } from "@/components/auth/auth-form";
import { Navbar } from "@/components/layout/navbar";
import { useSession } from "@/lib/auth-client";
import { setSupabaseSessionToken } from "@/lib/supabase";
import { seo } from "@/utils/seo";

const AUTH_TOKEN_COOKIE = "szese-auth-token";

// Server-side: verify Supabase session from cookie
const verifySession = createServerFn({ method: "GET" }).handler(async () => {
  const supabaseUrl = process.env.VITE_SUPABASE_URL ?? process.env.SUPABASE_URL ?? "";
  const supabaseAnonKey = process.env.VITE_SUPABASE_ANON_KEY ?? process.env.SUPABASE_ANON_KEY ?? "";

  if (!supabaseUrl || !supabaseAnonKey) return false;

  // Read the lightweight JWT cookie
  const request = getRequest();
  const cookieHeader = request?.headers.get("cookie") ?? "";
  let sessionToken: string | null = null;

  for (const part of cookieHeader.split(";")) {
    const [name, ...rest] = part.trim().split("=");
    if (name?.trim() === AUTH_TOKEN_COOKIE) {
      sessionToken = rest.join("=") || null;
      break;
    }
  }

  if (!sessionToken) return false;

  // Verify the JWT with Supabase
  const supabase = createClient(supabaseUrl, supabaseAnonKey, {
    auth: { autoRefreshToken: false, persistSession: false },
  });

  const { data, error } = await supabase.auth.getUser(sessionToken);
  return !error && !!data.user;
});

export const Route = createFileRoute("/_auth")({
  head: () => ({
    meta: [
      ...seo({
        title: "Setups by SZESE",
        noindex: true,
      }),
    ],
  }),
  beforeLoad: async ({ location }) => {
    // Only verify session on initial server-side render, not on client-side navigation.
    // The component handles auth client-side via useSession().
    if (typeof window === "undefined") {
      let isAuthenticated = false;
      try {
        isAuthenticated = await verifySession();
      } catch {
        // Verification failed
      }
      if (!isAuthenticated) {
        throw redirect({
          to: "/auth/signin",
          search: { redirect: location.pathname + (location.search ? `?${location.search}` : "") },
        });
      }
    }
  },
  component: RouteComponent,
});

function RouteComponent() {
  const session = useSession();
  const queryClient = useQueryClient();
  const wasAuthenticated = useRef(!!session.data?.user);

  // Identify authenticated user in PostHog
  useEffect(() => {
    if (session.data?.user) {
      posthog.identify(session.data.user.id, {
        email: session.data.user.email,
      });
      wasAuthenticated.current = true;
    }
  }, [session.data?.user?.id, session.data?.user?.email, session.data?.user]);

  // Clear query cache on sign-out
  useEffect(() => {
    if (!session.isPending && !session.data && wasAuthenticated.current) {
      wasAuthenticated.current = false;
      queryClient.clear();
      posthog.reset();
    }
  }, [session.isPending, session.data, queryClient]);

  // Inject Supabase access token for client-side RLS resolution
  useEffect(() => {
    const token = session.data?.session?.token ?? null;
    setSupabaseSessionToken(token);
    return () => setSupabaseSessionToken(null);
  }, [session.data?.session?.token]);

  return (
    <>
      {session.isPending ? (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : session.data ? (
        <div className="min-h-screen bg-background">
          <Navbar />
          <main className="p-6">
            <div className="mx-auto max-w-7xl">
              <PageTransition />
            </div>
          </main>
        </div>
      ) : (
        <AuthForm
          redirectTo={
            typeof window !== "undefined"
              ? window.location.pathname + window.location.search
              : "/app"
          }
        />
      )}
    </>
  );
}

function PageTransition() {
  const location = useLocation();
  // Use the first two path segments as key so sub-page navigation
  // within admin/driver doesn't trigger the animation
  const segments = location.pathname.split("/").filter(Boolean);
  const pageKey = segments.slice(0, 3).join("/");

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <motion.div
      key={pageKey}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.15 }}
    >
      <Outlet />
    </motion.div>
  );
}
